import { useMemo } from 'react'
import { Layer, Shape } from 'react-konva'
import type Konva from 'konva'
import { useEditor, activeLevel } from '@/state/store'
import type { PolygonPath } from '@/model/types'
import { cutGeometry, generateWalls, generateCompoundWall, compoundCenterline } from '@/lib/walls'
import { openingCutRects, compoundCutRects } from '@/lib/openings'

const EXTERIOR_FILL = '#d7dce8'
const EXTERIOR_STROKE = '#9aa3ba'
const INTERIOR_FILL = '#c4cbdc'
const INTERIOR_STROKE = '#9aa3ba'
const COMPOUND_FILL = '#b9b3a4'
const COMPOUND_STROKE = '#8f8a7d'

function drawPaths(ctx: Konva.Context, shape: Konva.Shape, paths: PolygonPath[]) {
  ctx.beginPath()
  for (const path of paths) {
    if (path.length < 2) continue
    ctx.moveTo(path[0].x, path[0].y)
    for (let i = 1; i < path.length; i++) ctx.lineTo(path[i].x, path[i].y)
    ctx.closePath()
  }
  // Non-zero winding: clipper emits holes with opposite orientation, so they render empty.
  ctx.fillStrokeShape(shape)
}

/** Renders auto-generated walls (filled bands, cut at openings) + the compound wall. Non-interactive. */
export function WallLayer() {
  const level = useEditor(activeLevel)
  const plot = useEditor((s) => s.project.plot)
  const standards = useEditor((s) => s.project.settings.standards)
  const scale = useEditor((s) => s.view.scale)

  const walls = useMemo(() => {
    const raw = generateWalls(level.spaces, standards.exteriorWallThickness, standards.interiorWallThickness)
    return cutGeometry(raw, openingCutRects(level.spaces, plot, standards))
  }, [level.spaces, plot, standards])

  const compound = useMemo(() => {
    const cw = plot.compoundWall
    if (!cw?.enabled) return []
    const ring = generateCompoundWall(compoundCenterline(plot.boundary, cw.inset), cw.thickness)
    return cutGeometry({ exterior: ring, interior: [] }, compoundCutRects(plot)).exterior
  }, [plot])

  const strokeW = 0.8 / scale

  if (walls.exterior.length === 0 && walls.interior.length === 0 && compound.length === 0) return null

  return (
    <Layer listening={false}>
      {compound.length > 0 && (
        <Shape
          fill={COMPOUND_FILL}
          stroke={COMPOUND_STROKE}
          strokeWidth={strokeW}
          sceneFunc={(ctx, shape) => drawPaths(ctx, shape, compound)}
        />
      )}
      <Shape
        fill={INTERIOR_FILL}
        stroke={INTERIOR_STROKE}
        strokeWidth={strokeW}
        sceneFunc={(ctx, shape) => drawPaths(ctx, shape, walls.interior)}
      />
      <Shape
        fill={EXTERIOR_FILL}
        stroke={EXTERIOR_STROKE}
        strokeWidth={strokeW}
        sceneFunc={(ctx, shape) => drawPaths(ctx, shape, walls.exterior)}
      />
    </Layer>
  )
}
